/* eslint-disable @typescript-eslint/no-explicit-any */
import { DeleteOutlined, MoreOutlined } from '@ant-design/icons'
import { IEvent } from 'app/api/event/model'
import { requestDeleteRewardActivity } from 'app/api/reward'
import { PermissionEnum } from 'app/common/enum'
import { BaseButton } from 'app/components/common/BaseButton'
import { BasePopconfirm } from 'app/components/common/BasePopconfirm'
import { BasePopover } from 'app/components/common/BasePopover'
import { BaseSpace } from 'app/components/common/BaseSpace'
import BaseText from 'app/components/common/BaseText'
import { BaseTableManagement } from 'app/components/tables/BaseTableManagement'
import {
  convertColumnTable,
  ExpandedTableValue,
} from 'app/components/tables/BaseTableReport/hook'
import { notificationController } from 'app/controllers/notification-controller'
import { useVerifyPermission } from 'app/helpers/permission.helper'
import { isEqual } from 'lodash'
import { useMemo, useState } from 'react'
import {
  DefineRewardType,
  IActivityEvent,
  IRewardActivity,
  RewardTypeEnum,
} from '../../../CreateEvent/type'
import { ModalUpdateReward } from '../../modal/reward'
import { ChildRowTeam } from './layouts'
import { ChildRow } from './layouts/ChildRow'

export const RewardLayout = ({
  activity,
}: {
  activity?: IActivityEvent
  event?: IEvent
}) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<number[]>([])

  const handleExpand = (expanded: boolean, newKey?: number) => {
    if (!newKey) return
    if (expanded) {
      setExpandedRowKeys([...expandedRowKeys, newKey])
    } else {
      setExpandedRowKeys(expandedRowKeys.filter(key => key !== newKey))
    }
  }

  const data = useMemo(() => {
    return activity?.rewards?.map((item, index) => {
      return {
        ...item,
        key: index + 1,
      }
    })
  }, [activity?.rewards])

  const { verifyPermission } = useVerifyPermission()

  const [loadingDelete, setLoadingDelete] = useState(false)
  const onPressDeleteReward = async (id?: number) => {
    if (!id) return
    try {
      setLoadingDelete(true)
      const res = await requestDeleteRewardActivity(id)
      if (res) {
        notificationController.success({ message: 'Thành công!' })
        window.location.reload()
      }
    } catch (error) {
      console.log({ error })
    } finally {
      setLoadingDelete(false)
    }
  }
  return (
    <BaseTableManagement
      dataSource={data}
      columns={[
        convertColumnTable<IRewardActivity & { key: number }>({
          key: 'key',
          title: 'STT',
          classNameWidthColumnOverwrite: 'number-column',
          render: (index: number) => {
            return (
              <BaseText children={index} fontWeight="medium" opacity="0.7" />
            )
          },
        }),
        convertColumnTable<IRewardActivity>({
          key: 'name',
          title: 'Tên giải',
          classNameWidthColumnOverwrite: 'big-column',
          render: (_, record) => {
            const isExpanded = expandedRowKeys.includes(record.key ?? 0)
            const _handleExpand = () => {
              handleExpand(!isExpanded, record?.key)
            }

            return (
              <ExpandedTableValue
                isExpanded={isExpanded}
                handleExpand={_handleExpand}
                value={record?.name}
                isHaveAreaWhenNotHaveActionExpanded
              />
            )
          },
        }),
        convertColumnTable<IRewardActivity>({
          key: 'description',
          title: 'Mô tả',
          render: (text?: string) => {
            return (
              <BaseText children={text} fontWeight="medium" opacity="0.7" />
            )
          },
        }),
        convertColumnTable<IRewardActivity>({
          key: 'gift',
          title: 'Phần quà',
          render: (text?: string) => {
            return (
              <BaseText children={text} fontWeight="medium" opacity="0.7" />
            )
          },
        }),
        convertColumnTable<IRewardActivity>({
          key: 'countPerReward',
          title: 'Số lượng giải',
          render: (text?: number) => {
            return (
              <BaseText children={text} fontWeight="medium" opacity="0.7" />
            )
          },
        }),
        convertColumnTable<IRewardActivity>({
          key: 'type',
          title: 'Đối tượng nhận giải',
          render: (type: RewardTypeEnum) => {
            return (
              <BaseText
                children={DefineRewardType[type]}
                fontWeight="medium"
                colorText="statesGreenColor"
              />
            )
          },
        }),
        ...(verifyPermission(PermissionEnum.UpdateEvent)
          ? [
              convertColumnTable<any>({
                key: 'key',
                title: '',
                fixed: 'right',
                classNameWidthColumnOverwrite: 'number-column',
                render: (_, record) => {
                  return (
                    <BasePopover
                      trigger={'click'}
                      content={
                        <BaseSpace size={8}>
                          <ModalUpdateReward reward={record} />
                          <BasePopconfirm
                            trigger={'click'}
                            title={'Bạn có thực sự muốn xoá?'}
                            onConfirm={() => onPressDeleteReward(record?.id)}
                          >
                            <BaseButton
                              type="text"
                              icon={
                                <DeleteOutlined
                                  style={{ color: 'red' }}
                                  rev={undefined}
                                />
                              }
                              loading={loadingDelete}
                              style={{
                                width: '100%',
                                justifyContent: 'flex-start',
                              }}
                            >
                              {'Xoá'}
                            </BaseButton>
                          </BasePopconfirm>
                        </BaseSpace>
                      }
                    >
                      <BaseButton
                        icon={<MoreOutlined rev={undefined} />}
                        type="text"
                      />
                    </BasePopover>
                  )
                },
              }),
            ]
          : []),
      ]}
      scroll={{ x: true }}
      pagination={{
        pageSize: 5,
      }}
      rowClassName="row-overwrite-style"
      expandable={{
        expandedRowKeys,
        onExpand: handleExpand,
        expandIcon: () => null,
        showExpandColumn: false,
        expandedRowRender: (reward: IRewardActivity) =>
          isEqual(reward?.type, RewardTypeEnum.GROUP) ? (
            <ChildRowTeam
              reward={reward}
              actionId={activity?.id}
              teams={activity?.teams ?? []}
            />
          ) : (
            <ChildRow reward={reward} actionId={activity?.id} />
          ),
      }}
    />
  )
}
